import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Card } from "@/components/ui/card";
import { Compass, CheckCircle2 } from "lucide-react";
import client from "@/api/client";

function fmtDate(iso, lang) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(lang || undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return iso;
  }
}

/**
 * JourneyLinkCard — shows the visitor's original Journey session on
 * their Dashboard. Renders nothing when there is no linked session
 * (users who signed up without going through the Journey funnel).
 */
export function JourneyLinkCard() {
  const { t, i18n } = useTranslation();
  const [session, setSession] = useState(null);
  const [loaded, setLoaded] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await client.get("/journey/me");
      setSession(res.data?.session || null);
    } catch {
      setSession(null);
    } finally {
      setLoaded(true);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!loaded || !session) return null;

  const slug = (session.business_type_slug || "").replace(/-/g, " ") || t("journey.unknownType");

  return (
    <Card
      className="rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/8 to-transparent p-5 space-y-3"
      data-testid="dashboard-journey-card"
    >
      <div className="flex items-center gap-2">
        <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/12 text-primary">
          <Compass className="h-4 w-4" />
        </span>
        <div className="min-w-0 flex-1">
          <h3 className="font-display font-semibold text-sm">
            {t("journey.linkTitle")}
          </h3>
          <p className="text-[11px] text-muted-foreground truncate">
            {t("journey.linkSubtitle")}
          </p>
        </div>
        <span className="inline-flex h-6 items-center gap-1 rounded-full bg-emerald-500/10 text-emerald-700 px-2 text-[10px] font-semibold uppercase tracking-widest">
          <CheckCircle2 className="h-3 w-3" /> {t("journey.convertedBadge")}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3 pt-1">
        <div
          className="rounded-xl border border-border p-3"
          data-testid="dashboard-journey-type"
        >
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("journey.businessType")}
          </p>
          <p className="text-sm font-medium capitalize mt-1 truncate">{slug}</p>
        </div>
        <div
          className="rounded-xl border border-border p-3"
          data-testid="dashboard-journey-converted-at"
        >
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground">
            {t("journey.convertedOn")}
          </p>
          <p className="text-sm font-medium mt-1 font-mono" dir="ltr">
            {fmtDate(session.converted_at, i18n.language)}
          </p>
        </div>
      </div>
    </Card>
  );
}

export default JourneyLinkCard;
